import { saveDefaultsToSource, type SaveDefaultsResult } from '../dev/saveDefaults';
import defaults from './Camera.defaults.json';
import type { FollowCamera } from './Camera';

export type CameraNumericKey =
  | 'height'
  | 'tiltDeg'
  | 'topLookAhead'
  | 'topLeadResponsiveness'
  | 'topLeadFullSpeed'
  | 'topFollowResponsiveness'
  | 'topFovDeg'
  | 'chaseDistance'
  | 'chaseHeight'
  | 'chaseLookAhead'
  | 'chaseTargetHeight'
  | 'chaseFovDeg'
  | 'chaseFovBoostDeg'
  | 'chaseFovFullSpeed'
  | 'chaseFovResponsiveness'
  | 'chaseHeadingVelocityBlend'
  | 'chaseHeadingDeadzoneYawRate'
  | 'chaseHeadingDeadzoneLateralSpeed'
  | 'chaseMaxYawLagDeg'
  | 'chaseTurnHeightBoost'
  | 'chaseTurnHeightFullLateral'
  | 'apexLookAhead'
  | 'apexLookAheadSpeedGain'
  | 'apexMaxLateralOffset'
  | 'apexOffTrackFadeStart'
  | 'apexOffTrackFadeEnd'
  | 'lookSteerBias'
  | 'cameraLateralLean'
  | 'bankMaxRollDeg'
  | 'bankHalflife'
  | 'highSpeedShakeAmp'
  | 'chaseVelocityCompensation'
  | 'accelLagMaxDistance'
  | 'accelLagResponsiveness'
  | 'accelLagReturnResponsiveness'
  | 'chasePositionResponsiveness'
  | 'chaseLookResponsiveness';

export type CameraBooleanKey = 'apexAimEnabled';
export type CameraTuningKey = CameraNumericKey | CameraBooleanKey;
export type CameraTuning = Record<CameraNumericKey, number> & Record<CameraBooleanKey, boolean>;

// Bumped to v8 (no legacy migration) to orphan any stale caches that were
// shadowing the saved Camera.defaults.json after the localStorage-shadow
// fix landed. JSON is the source of truth on first boot post-bump; future
// "Save to code" presses also write to v8 so values persist across reloads.
export const CAMERA_TUNING_STORAGE_KEY = 'twin-suns-turbo:rig-review-camera-tuning:v8';
const LEGACY_CAMERA_TUNING_STORAGE_KEYS: string[] = [];
export const CAMERA_DEFAULTS_FILE = 'src/game/Camera.defaults.json' as const;

export const DEFAULT_CAMERA_TUNING: CameraTuning = defaults as CameraTuning;

export function cameraNumericKeys(): CameraNumericKey[] {
  return [
    'height',
    'tiltDeg',
    'topLookAhead',
    'topLeadResponsiveness',
    'topLeadFullSpeed',
    'topFollowResponsiveness',
    'topFovDeg',
    'chaseDistance',
    'chaseHeight',
    'chaseLookAhead',
    'chaseTargetHeight',
    'chaseFovDeg',
    'chaseFovBoostDeg',
    'chaseFovFullSpeed',
    'chaseFovResponsiveness',
    'chaseHeadingVelocityBlend',
    'chaseHeadingDeadzoneYawRate',
    'chaseHeadingDeadzoneLateralSpeed',
    'chaseMaxYawLagDeg',
    'chaseTurnHeightBoost',
    'chaseTurnHeightFullLateral',
    'apexLookAhead',
    'apexLookAheadSpeedGain',
    'apexMaxLateralOffset',
    'apexOffTrackFadeStart',
    'apexOffTrackFadeEnd',
    'lookSteerBias',
    'cameraLateralLean',
    'bankMaxRollDeg',
    'bankHalflife',
    'highSpeedShakeAmp',
    'chaseVelocityCompensation',
    'accelLagMaxDistance',
    'accelLagResponsiveness',
    'accelLagReturnResponsiveness',
    'chasePositionResponsiveness',
    'chaseLookResponsiveness',
  ];
}

export function cameraBooleanKeys(): CameraBooleanKey[] {
  return ['apexAimEnabled'];
}

export function readCameraTuning(camera: FollowCamera): CameraTuning {
  const tuning = {} as CameraTuning;
  for (const key of cameraNumericKeys()) tuning[key] = camera[key];
  for (const key of cameraBooleanKeys()) tuning[key] = camera[key];
  return tuning;
}

export function loadCachedCameraTuning(): Partial<CameraTuning> {
  const keys = [CAMERA_TUNING_STORAGE_KEY, ...LEGACY_CAMERA_TUNING_STORAGE_KEYS];
  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const source = JSON.parse(raw) as Record<string, unknown>;
      const tuning: Partial<CameraTuning> = {};
      for (const tuningKey of cameraNumericKeys()) {
        const value = source[tuningKey];
        if (typeof value === 'number' && Number.isFinite(value)) {
          tuning[tuningKey] = migrateCachedCameraNumber(tuningKey, value);
        }
      }
      for (const tuningKey of cameraBooleanKeys()) {
        const value = source[tuningKey];
        if (typeof value === 'boolean') tuning[tuningKey] = value;
      }
      if (key !== CAMERA_TUNING_STORAGE_KEY && Object.keys(tuning).length > 0) {
        saveCachedCameraTuning(tuning);
      }
      return tuning;
    } catch {
      continue;
    }
  }
  return {};
}

function migrateCachedCameraNumber(
  key: CameraNumericKey,
  value: number,
): number {
  // v4 and earlier stored these as exponential responsiveness rates (5..11).
  // v5 reuses the public property names but treats them as spring halflives
  // in seconds. Carrying the old values forward makes chase mode lag by many
  // seconds. Any value above the v5 GUI range is invalid and resets to the
  // tuned default, even if it was already copied into v5 by a local session.
  if (
    key === 'chasePositionResponsiveness'
    || key === 'chaseLookResponsiveness'
    || key === 'chaseFovResponsiveness'
  ) {
    const defaultValue = DEFAULT_CAMERA_TUNING[key];
    if (value > 1.5 || value <= 0) return defaultValue;
  }

  return value;
}

export function saveCachedCameraTuning(tuning: Partial<CameraTuning>): void {
  try {
    window.localStorage.setItem(CAMERA_TUNING_STORAGE_KEY, JSON.stringify(tuning));
  } catch (err) {
    console.warn('[camera-config] failed to cache camera tuning:', err);
  }
}

/** Wipe the cached tuning. Caller should re-apply defaults to the live camera. */
export function clearCachedCameraTuning(): void {
  try {
    window.localStorage.removeItem(CAMERA_TUNING_STORAGE_KEY);
    for (const key of LEGACY_CAMERA_TUNING_STORAGE_KEYS) {
      window.localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

/** Reset a live camera's tunable fields to the committed defaults. */
export function applyDefaultCameraTuning(camera: FollowCamera): void {
  for (const key of cameraNumericKeys()) {
    const value = DEFAULT_CAMERA_TUNING[key];
    if (typeof value === 'number' && Number.isFinite(value)) camera[key] = value;
  }
  for (const key of cameraBooleanKeys()) {
    const value = DEFAULT_CAMERA_TUNING[key];
    if (typeof value === 'boolean') camera[key] = value;
  }
}

export function applyCachedCameraTuning(
  camera: FollowCamera,
  tuning: Partial<CameraTuning> = loadCachedCameraTuning(),
): void {
  for (const key of cameraNumericKeys()) {
    const value = tuning[key];
    if (typeof value === 'number' && Number.isFinite(value)) camera[key] = value;
  }
  for (const key of cameraBooleanKeys()) {
    const value = tuning[key];
    if (typeof value === 'boolean') camera[key] = value;
  }
}

/** Persist the camera's current tuning to `Camera.defaults.json` via dev middleware. */
export function saveCameraDefaultsToSource(
  tuning: CameraTuning,
): Promise<SaveDefaultsResult> {
  return saveDefaultsToSource(CAMERA_DEFAULTS_FILE, tuning);
}
